import { CSSResultGroup, TemplateResult, html } from 'lit';
import { LightDomStyles, OutlineElement } from '@phase2/outline-core';
import { customElement, query } from 'lit/decorators.js';
import componentStyles from './outline-media-gallery.css.lit';
import '../outline-button-baseline/outline-button-baseline';
import '../outline-icon-baseline/outline-icon-baseline';
import buttonStyles from '../outline-button-baseline/outline-button-baseline.css.lit';
import Splide from '@splidejs/splide';

type SplideObject = {
  slide: HTMLElement;
};
/**
 * The OutlineMediaGallery component
 * @element outline-media-gallery
 */
@customElement('outline-media-gallery')
export class OutlineMediaGallery extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, buttonStyles];
  lightDomStyles = new LightDomStyles(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  splide: Splide;

  @query('.splide')
  splideEl: HTMLElement;

  @query('.splide__arrow--prev')
  prevBtn: HTMLElement;

  @query('.splide__arrow--next')
  nextBtn: HTMLElement;

  observer: MutationObserver;

  connectedCallback() {
    super.connectedCallback();

    this.observer = new MutationObserver(() => {
      this.requestUpdate();
    });
    this.observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  firstUpdated() {
    const slideLength = this.querySelectorAll('[slot="slide"]').length;

    this.splide = new Splide(this.splideEl, {
      keyboard: false,
      arrows: slideLength > 1,
    }).mount();

    if (slideLength <= 1) return;

    // Adds spans to buttons for screen readers
    this.prevBtn.innerHTML += '<span class="sr-only">Previous Slide</span>';
    this.nextBtn.innerHTML += '<span class="sr-only">Next Slide</span>';

    // Add event listener for slide change.
    this.splide.on('hidden', this._handleSlideChange.bind(this));
  }

  render(): TemplateResult {
    return html`
      <div class="media-gallery">
        <div class="splide image-list">
          <div class="splide__track">
            <div class="splide__list">
              ${(
                Array.from(
                  this.querySelectorAll(`[slot='slide']`)
                ) as HTMLElement[]
              ).map((slideElementLightDOM, index, LightDOMElements) => {
                const videoElement = slideElementLightDOM.querySelector(
                  '[media-type="video"]'
                );
                const hasVideo = videoElement !== null;
                const isExternalVideo =
                  videoElement?.querySelector('iframe') !== null;
                const hasDescription =
                  slideElementLightDOM.querySelector('[media-description]') !==
                  null;

                return html`
                  <div
                    class="splide__slide ${hasDescription
                      ? 'has-description'
                      : ''}"
                    role="group"
                    aria-label="slide ${index +
                    1} of ${LightDOMElements.length}"
                  >
                    ${hasVideo && !isExternalVideo
                      ? html`
                          <button
                            class="trigger ${hasDescription
                              ? 'has-description'
                              : ''}"
                            aria-label="Play Video"
                            focusable="true"
                            @keydown="${(e: KeyboardEvent) =>
                              this._handleTriggerKeydown(e)}"
                            @click="${(e: MouseEvent) => this._playVideo(e)}"
                          >
                            <div class="video-wrapper">
                              <div class="play-overlay">
                                <div class="btn primary large rounded">
                                  <outline-icon-baseline
                                    name="eu-triangle"
                                    size="2rem"
                                  ></outline-icon-baseline>
                                </div>
                              </div>
                              ${slideElementLightDOM
                                .querySelector('[media-type="image"]')
                                ?.cloneNode(true)}
                            </div>
                            <div
                              class="media-gallery-video"
                              hidden
                              aria-hidden="true"
                            >
                              ${slideElementLightDOM
                                .querySelector('[media-type="video"]')
                                ?.cloneNode(true)}
                            </div>
                          </button>
                        `
                      : videoElement?.cloneNode(true) ||
                        slideElementLightDOM
                          .querySelector('[media-type="image"]')
                          ?.cloneNode(true)}
                    ${hasDescription
                      ? html`
                          <div class="media-gallery-description">
                            ${slideElementLightDOM
                              .querySelector('[media-description]')
                              ?.cloneNode(true)}
                          </div>
                        `
                      : null}
                  </div>
                `;
              })}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Plays the video when the trigger element is clicked.
   *
   * @param {Event} event - The event that triggered the modal closure.
   * @returns {void}
   */
  _playVideo = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const video = target?.querySelector('video') as
      | HTMLVideoElement
      | undefined;
    const videoWrapper = target?.querySelector('.video-wrapper') as HTMLElement;
    const mediaGalleryVideo = target?.querySelector(
      '.media-gallery-video'
    ) as HTMLElement;

    if (video) {
      video.play();
      videoWrapper?.setAttribute('aria-hidden', 'true');
      mediaGalleryVideo?.removeAttribute('aria-hidden');
      videoWrapper?.setAttribute('hidden', 'true');
      mediaGalleryVideo?.removeAttribute('hidden');
    }
  };

  /**
   * Handles keyboard events for modal triggers, specifically the "Enter" key.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered.
   * @returns {void}
   */
  _handleTriggerKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this._playVideo(event);
    }
  };

  /**
   * Handles the slide change event and pauses any playing video.
   *
   * @param {SplideObject} slideObject - The Splide object for the current slide.
   *
   * @returns {void}
   */
  _handleSlideChange = (slideObject: SplideObject): void => {
    const video = slideObject.slide.querySelector('video') as HTMLVideoElement;
    if (video && !video.paused) {
      video.pause();
    }
  };
}
